import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import StudentService from "../../services/student.service";

// =========================================================
// GET STUDENT LIST
// =========================================================

export const getStudent = createAsyncThunk(
  "student/getStudent",
  async (payload, thunkAPI) => {
    try {
      const response = await StudentService.getStudent(payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// =========================================================
// DELETE STUDENT
// =========================================================

export const deleteStudent = createAsyncThunk(
  "student/deleteStudent",
  async (id, thunkAPI) => {
    try {
      await StudentService.deleteStudent(id);

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// =========================================================
// GET ALL CHILDREN
// =========================================================

export const getallstudent = createAsyncThunk(
  "student/getallStudent",
  async (parentId, thunkAPI) => {
    try {
      const response = await StudentService.getallchild(parentId);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// =========================================================
// ADD STUDENT
// =========================================================

export const addStudent = createAsyncThunk(
  "student/addStudent",
  async (data, thunkAPI) => {
    try {
      const response = await StudentService.addStudent(data);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// =========================================================
// UPDATE STUDENT
// =========================================================

export const updateStudent = createAsyncThunk(
  "student/updateStudent",
  async (payload, thunkAPI) => {
    try {
      const response = await StudentService.updateStudent(payload);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// =========================================================
// INITIAL STATE
// =========================================================

const initialState = {
  loading: false,
  error: null,

  studentList: [],
  studentlist: [],

  selectedStudent: null,

  addSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

// =========================================================
// SLICE
// =========================================================

const studentSlice = createSlice({
  name: "student",

  initialState,

  reducers: {
    resetUpdateStatus: (state) => {
      state.updateSuccess = false;
    },

    resetAddStatus: (state) => {
      state.addSuccess = false;
    },

    resetDeleteStatus: (state) => {
      state.deleteSuccess = false;
    },

    clearStudentError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // =====================================================
      // GET STUDENT
      // =====================================================

      .addCase(getStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getStudent.fulfilled, (state, action) => {
        state.loading = false;

        state.studentList =
          action.payload?.data || [];
      })

      .addCase(getStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =====================================================
      // GET ALL STUDENTS / CHILDREN
      // =====================================================

      .addCase(getallstudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getallstudent.fulfilled, (state, action) => {
        state.loading = false;

        state.studentlist =
          action.payload?.data || [];
      })

      .addCase(getallstudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =====================================================
      // ADD STUDENT
      // =====================================================

      .addCase(addStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.addSuccess = false;
      })

      .addCase(addStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.addSuccess = true;

        const newStudent = action.payload?.data;

        if (newStudent) {
          state.studentList.push(newStudent);
        }
      })

      .addCase(addStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.addSuccess = false;
      })

      // =====================================================
      // UPDATE STUDENT
      // =====================================================

      .addCase(updateStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.updateSuccess = false;
      })

      .addCase(updateStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.updateSuccess = true;

        const updated = action.payload?.data;

        if (!updated) return;

        // Update main student list
        const index = state.studentList.findIndex(
          (student) =>
            String(student._id || student.id) ===
            String(updated._id || updated.id)
        );

        if (index !== -1) {
          state.studentList[index] = updated;
        }

        // Update child/student list also
        const childIndex = state.studentlist.findIndex(
          (student) =>
            String(student._id || student.id) ===
            String(updated._id || updated.id)
        );

        if (childIndex !== -1) {
          state.studentlist[childIndex] = updated;
        }
      })

      .addCase(updateStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.updateSuccess = false;
      })

      // =====================================================
      // DELETE STUDENT
      // =====================================================

      .addCase(deleteStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.deleteSuccess = false;
      })

      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteSuccess = true;

        const deletedId = action.payload;

        // Remove from studentList
        state.studentList = state.studentList.filter(
          (student) =>
            String(student._id || student.id) !==
            String(deletedId)
        );

        // Remove from child/student list
        state.studentlist = state.studentlist.filter(
          (student) =>
            String(student._id || student.id) !==
            String(deletedId)
        );
      })

      .addCase(deleteStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.deleteSuccess = false;
      });
  },
});

// =========================================================
// ACTIONS
// =========================================================

export const {
  resetUpdateStatus,
  resetAddStatus,
  resetDeleteStatus,
  clearStudentError,
} = studentSlice.actions;

export default studentSlice.reducer;